import useAPI, { useAPIMutation } from "@/services/api/hook";
import {
  APIObject,
  APILookupValue,
  ExtendedUseQueryResult,
  Filters,
  SearchQuery
} from "@/services/api/types";
import { useEffect, useMemo } from "react";
import useBoundStore, {
  MAX_ENTITIES_PER_PAGE
} from "@/services/store/BoundStore";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/services/auth/AuthContext";
import {
  ReservedUserIds,
  unknownUser,
  UserData,
  UserStatsData,
  UserRolesResponse,
  UserProvisioningResponse,
  UserDepartmentsResponse,
  UserAreaOfResponsibilityResponse,
  UserTransferRequest,
  UserTransferResult
} from "./types";
import { filterReservedUserIds } from "./helpers";
import { EntitySliceState } from "@/services/api/entitystore";
import { useEntities } from "@/services/api/entityhooks";
import { createUseFilteredEntitiesInfinite } from "@/services/api/infinite";
import { forEach } from "lodash";

export const MAX_USERS_PER_PAGE = MAX_ENTITIES_PER_PAGE;

export const useUsers = (
  ids: string[],
  options?: { enabled: boolean }
): {
  users: UserData[];
  error: string | null;
  isSuccess: boolean;
  isLoading: boolean;
} => {
  const useUsersStore = (): EntitySliceState<UserData> => {
    return useBoundStore((state) => state.users);
  };

  const { entities, error, isSuccess, isLoading } = useEntities<UserData>(
    ids,
    "users",
    useUsersStore,
    (endpoint, params) => useAPI(endpoint, params, options),
    filterReservedUserIds
  );

  const users = useMemo(() => {
    return ids.map((id) => {
      if (id === ReservedUserIds.UNKNOWN_USER_ID) {
        return unknownUser;
      }
      return entities.find((user) => user?.id === id) ?? unknownUser;
    });
  }, [ids, entities]);

  return {
    users,
    error,
    isSuccess,
    isLoading
  };
};

export const useUsersMe = (): ExtendedUseQueryResult<UserData> => {
  const addUser = useBoundStore((state) => state.users.addEntity);
  const { user } = useAuth();
  const navigate = useNavigate();
  const result = useAPI("users/me", {}, { retry: false });
  const userId = user?.email || "unknown";

  useEffect(() => {
    if (result.isSuccess && result.data) {
      addUser(result.data);
    }
  }, [result.data, addUser]);

  useEffect(() => {
    if (result.error) {
      console.error("Failed to fetch user data:", result.error);
      if (result.error.code === "ERR_BAD_RESPONSE") {
        navigate({ pathname: "/loginErrorPage", search: `?user=${userId}` });
      }
    }
  }, [result.error, userId]);

  return result;
};

export const useUsersAll = (): {
  users: UserData[];
  error: string | null;
  isSuccess: boolean;
  isLoading: boolean;
} => {
  const addUser = useBoundStore((state) => state.users.addEntity);
  const entities = useBoundStore((state) => state.users.entities);
  const result = useAPI(
    "users/all",
    { queryParams: { size: "100" } },
    { retry: false }
  );

  useEffect(() => {
    if (result.isSuccess && result.data && Array.isArray(result.data.users)) {
      forEach(result.data.users, (user: UserData) => {
        addUser(user);
      });
    }
  }, [result.data, result.isSuccess]);

  const users = useMemo(() => {
    if (result.isSuccess && Array.isArray(result.data?.users)) {
      return result.data.users;
    }
    return Object.values(entities || {});
  }, [result.data, result.isSuccess, entities]);

  return {
    users,
    error: result.error ? String(result.error) : null,
    isSuccess: result.isSuccess,
    isLoading: result.isLoading
  };
};

export const {
  useFilteredEntitiesInfinite,
  useResetFilteredEntitiesInfinite: useResetUsersInfinite
} = createUseFilteredEntitiesInfinite<UserData>("users", MAX_USERS_PER_PAGE);

export const useUsersInfinite = (
  filters?: Filters,
  search?: SearchQuery,
  groupedBy?: keyof UserData,
  pageSize = MAX_USERS_PER_PAGE,
  sortField?: string,
  sortAsc?: boolean,
  minSearchCharacters?: number
) => {
  return useFilteredEntitiesInfinite(
    () => ({ addEntity: useBoundStore((state) => state.users.addEntity) }),
    {
      filters,
      search,
      groupedBy,
      pageSize,
      sortField,
      sortAsc,
      minSearchCharacters
    }
  );
};

export interface BulkUpdateItem {
  id: string;
  [key: string]: any;
}

export type BulkUpdateRequest = BulkUpdateItem[];

export interface BulkUpdateResponse {
  success: boolean;
  updatedUsers: UserData[];
  errors?: Array<{
    id: string;
    error: string;
  }>;
}

export const useBulkUpdateUsers = () => {
  const updateEntity = useBoundStore((state) => state.users.updateEntity);

  return useAPIMutation<BulkUpdateRequest, BulkUpdateResponse>(
    "users/bulk",
    { method: "PUT" },
    (data: BulkUpdateResponse) => {
      if (data.updatedUsers) {
        data.updatedUsers.forEach((user: UserData) => {
          updateEntity(user.id, user);
        });
      }
    }
  );
};

export const useTransferUserRecords = () =>
  useAPIMutation<UserTransferRequest, UserTransferResult>("users/transfer", {
    method: "POST"
  });

export const useRoles = (): ExtendedUseQueryResult<UserRolesResponse> =>
  useAPI("users/roles");

// An account's own roles have their own ids, so role checks compare the type
// the user's role grants. Built-in roles share their id with their type,
// which is also the fallback while the roles lookup has no type for the role.
// isResolved is true only once both reads have data, so a negative answer
// (no access) is never drawn from a lookup that failed or hasn't run; a failed
// read is reported through isError with a retry instead.
export const useMyRoleType = (): {
  roleType: number | undefined;
  isLoading: boolean;
  isResolved: boolean;
  isError: boolean;
  retry: () => void;
} => {
  const currentUserQuery = useUsersMe();
  const rolesQuery = useRoles();
  const currentUser = currentUserQuery.data;
  const rolesData = rolesQuery.data;
  const role = rolesData?.roles.find(
    (candidateRole) => candidateRole.id === currentUser?.role
  );
  return {
    roleType: role?.type ?? currentUser?.role,
    isLoading: currentUserQuery.isLoading || rolesQuery.isLoading,
    isResolved: currentUser !== undefined && rolesData !== undefined,
    isError:
      (currentUser === undefined && currentUserQuery.isError) ||
      (rolesData === undefined && rolesQuery.isError),
    retry: () => {
      if (currentUserQuery.isError) currentUserQuery.refetch();
      if (rolesQuery.isError) rolesQuery.refetch();
    }
  };
};

export const useUserProvisioning =
  (): ExtendedUseQueryResult<UserProvisioningResponse> =>
    useAPI("users/provisioning");

export const useCreateUserMutation = () => {
  const addUser = useBoundStore((state) => state.users.addEntity);
  return useAPIMutation<UserData, APIObject>(
    "createUser",
    {
      method: "POST"
    },
    (response, data) => {
      addUser({
        ...data,
        id: response.id
      } as UserData);
    }
  );
};

export const useUpdateUser = () => {
  const updateEntity = useBoundStore((state) => state.users.updateEntity);

  return useAPIMutation<
    {
      pathParams: {
        userId: string;
      };
      data: Partial<UserData>;
    },
    UserData
  >(
    "users/update",
    {
      method: "PUT"
    },
    (updatedUser, requestData) => {
      if (updatedUser) {
        updateEntity(requestData.pathParams.userId, updatedUser);
      }
    }
  );
};

export const useUsersStats = (userIds: string[]): UserStatsData => {
  const result = useAPI(
    "users/stats",
    {
      queryParams: {
        ids: userIds.join(",")
      }
    },
    {
      enabled: userIds.length > 0,
      retry: false
    }
  );

  const stats = useMemo(() => {
    if (result.isSuccess && result.data) {
      return result.data as UserStatsData;
    }
    return { companies: 0, deals: 0, leads: 0 };
  }, [result.data, result.isSuccess]);

  return stats;
};

export const useUserStats = (userId: string): UserStatsData => {
  return useUsersStats([userId]);
};

export const useAreaOfResponsibility =
  (): ExtendedUseQueryResult<UserAreaOfResponsibilityResponse> =>
    useAPI("users/areaOfResponsibility");

export const useAreaOfResponsibilityUpsert = (
  onSuccess?: (response: APILookupValue) => void
) => {
  return useAPIMutation<{ name: string }, APILookupValue>(
    "users/areaOfResponsibility/upsert",
    {
      method: "PUT"
    },
    onSuccess
  );
};

export const useDepartments =
  (): ExtendedUseQueryResult<UserDepartmentsResponse> =>
    useAPI("users/departments");
